import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '@lib/useUrlState';
import { Briefcase, Plus, RefreshCw, ChevronUp, ChevronDown as ChevronDownIcon } from 'lucide-react';

const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

const STATO_OPTIONS = ['tutte', 'aperta', 'chiusa', 'sospesa'];

const fmtCurrency = (n: number | null) =>
  n != null
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n)
    : '—';

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('it-IT') : '—';

const statoBadgeClass = (stato: string) => {
  const map: Record<string, string> = {
    aperta: 'bg-green-100 text-green-800',
    chiusa: 'bg-gray-100 text-gray-700',
    sospesa: 'bg-amber-100 text-amber-800',
  };
  return map[stato] ?? 'bg-gray-100 text-gray-700';
};

interface Commessa {
  id: string;
  codice: string;
  nome: string;
  stato: string;
  budget: number | null;
  data_inizio: string | null;
  data_fine: string | null;
}

interface NuovaCommessaForm {
  codice: string;
  nome: string;
  stato: string;
  budget: string;
  data_inizio: string;
  data_fine: string;
}

const emptyForm = (): NuovaCommessaForm => ({
  codice: '',
  nome: '',
  stato: 'aperta',
  budget: '',
  data_inizio: '',
  data_fine: '',
});

const CommesseList: React.FC = () => {
  const navigate = useNavigate();
  const [commesse, setCommesse] = useState<Commessa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroStato, setFiltroStato] = useUrlState('stato', 'tutte');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<NuovaCommessaForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const caricaCommesse = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '50', offset: '0' });
      if (filtroStato !== 'tutte') params.set('stato', filtroStato);
      const res = await fetch(`/api/v1/centri-costo/commesse?${params}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore caricamento commesse: ${res.status}`);
      const data: Commessa[] = await res.json();
      setCommesse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [filtroStato]);

  useEffect(() => {
    caricaCommesse();
  }, [caricaCommesse]);

  const handleSave = useCallback(async () => {
    if (!form.codice.trim() || !form.nome.trim()) {
      setSaveError('Codice e nome sono obbligatori');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const payload: Record<string, unknown> = {
        codice: form.codice,
        nome: form.nome,
        stato: form.stato,
      };
      if (form.budget) payload.budget = parseFloat(form.budget);
      if (form.data_inizio) payload.data_inizio = form.data_inizio;
      if (form.data_fine) payload.data_fine = form.data_fine;

      const res = await fetch('/api/v1/centri-costo/commesse', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.detail ?? `Errore creazione: ${res.status}`);
      }
      setForm(emptyForm());
      setShowForm(false);
      await caricaCommesse();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(false);
    }
  }, [form, caricaCommesse]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-gray-400" />
          <h1 className="text-2xl font-semibold">Commesse</h1>
        </div>
        <button
          className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          onClick={() => setShowForm((v) => !v)}
        >
          {showForm ? <ChevronUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          Nuova commessa
        </button>
      </div>

      {/* Inline form */}
      {showForm && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">Nuova commessa</h2>
          {saveError && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
              {saveError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Codice *</label>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.codice}
                onChange={(e) => setForm((f) => ({ ...f, codice: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
              <input
                type="text"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Stato</label>
              <select
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.stato}
                onChange={(e) => setForm((f) => ({ ...f, stato: e.target.value }))}
              >
                {['aperta', 'chiusa', 'sospesa'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Budget (€)</label>
              <input
                type="number"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.budget}
                onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data inizio</label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.data_inizio}
                onChange={(e) => setForm((f) => ({ ...f, data_inizio: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Data fine</label>
              <input
                type="date"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.data_fine}
                onChange={(e) => setForm((f) => ({ ...f, data_fine: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
              onClick={() => { setShowForm(false); setForm(emptyForm()); setSaveError(null); }}
            >
              Annulla
            </button>
            <button
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Salvataggio…' : 'Crea commessa'}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-600">Stato:</label>
        <select
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          value={filtroStato}
          onChange={(e) => setFiltroStato(e.target.value)}
        >
          {STATO_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Caricamento…
        </div>
      ) : commesse.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400 gap-3">
          <Briefcase className="h-10 w-10 opacity-30" />
          <p className="text-sm">Nessuna commessa trovata.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Codice</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Stato</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Budget</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Inizio</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Fine</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {commesse.map((c) => (
                <tr
                  key={c.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/centri-costo/commesse/${c.id}`)}
                >
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{c.codice}</td>
                  <td className="px-4 py-2.5 font-medium text-gray-800">{c.nome}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statoBadgeClass(c.stato)}`}>
                      {c.stato}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{fmtCurrency(c.budget)}</td>
                  <td className="px-4 py-2.5 text-gray-600">{fmtDate(c.data_inizio)}</td>
                  <td className="px-4 py-2.5 text-gray-600">{fmtDate(c.data_fine)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CommesseList;
